import Cookies from 'js-cookie';
import React from 'react';
import { useSelector } from 'react-redux';

import { CartIcon } from '@app/components/icons';

import { Autocomplete } from './algolia-autocomplete';
import {
	MarketingTemplate,
	getMarketingTemplateConfig,
	marketingItems,
} from './marketing-template';
import { ProductItem } from './product-item';
import { ModesRadioButton } from './radio-buttons';

// function that sets isTablet to true or false based on the screen size
function useTabletStatus() {
	const [isMobile, setIsMobile] = React.useState(false);
	const checkIfMobile = () => {
		const width = window.innerWidth;
		if (width < 992) {
			setIsMobile(true);
		} else {
			setIsMobile(false);
		}
	};

	React.useEffect(() => {
		checkIfMobile();
		window.addEventListener('resize', checkIfMobile);
		return () => {
			window.removeEventListener('resize', checkIfMobile);
		};
	}, []);

	return isMobile;
}

// The popup that will be shown when the search icon is clicked

const MobileUtilitySearch = ({ searchModes, onSearchClose, mergeState }) => {
	const searchFormRef = React.useRef();
	const handleSearchSubmit = (e) => {
		e.preventDefault();

		const searchModeId = e.target['search-mode'].value || 'all';
		const searchMode = searchModes.find((mode) => mode.id === searchModeId);

		if (!searchMode || !searchFormRef.current) {
			return false;
		}

		const { action } = searchMode;
		const query = encodeURIComponent(e.target.search.value);

		window.location.href = `${action}?search=${query}`;
		return false;
	};

	// eslint-disable-next-line @typescript-eslint/no-shadow
	const selectedModes = useSelector((state) => state.app.selectedModes);

	const autocompleteTemplates = {
		// eslint-disable-next-line react/prop-types
		item: ({ item, components }) => (
			<ProductItem hit={item} components={components} />
		),
		components: {},
	};

	const [selectedMode, setSelectedMode] = React.useState(
		searchModes.find((mode) => mode.id === selectedModes[0]),
	);
	React.useEffect(() => {
		const cookieSelectedMode = Cookies.get('selectedMode');
		const newSelectedMode = searchModes.find(
			(mode) => mode.id === cookieSelectedMode || mode.id === selectedModes[0],
		);

		if (newSelectedMode) {
			if (cookieSelectedMode) {
				setSelectedMode(newSelectedMode);
			}
		}
	}, [selectedMode, searchModes, selectedModes]);

	return (
		<>
			<div id="mobile-search-nav" className="mobile-search-overlay">
				<div className="mobile-search-overlay-content">
					<button
						type="button"
						className="btn btn--close"
						onClick={onSearchClose}
					>
						<svg
							className="u-icon-circle-close-dims"
							role="presentation"
							aria-hidden="true"
						>
							<use xlinkHref="#circle-close" />
						</svg>
					</button>
					{searchModes && (
						<div className="utility-nav__link">
							<form
								onSubmit={handleSearchSubmit}
								className="mobile-search-form"
								ref={searchFormRef}
							>
								<div className="mobile-search-form__search-field input__wrapper">
									<li className="utility-nav__item">
										<Autocomplete
											openOnFocus
											selectedMode={selectedMode}
											getSources={() => [
												{
													sourceId: 'products',
													getItems() {
														return [];
													},
													templates: autocompleteTemplates,
												},
												getMarketingTemplateConfig(
													'marketing_template',
													marketingItems,
													{ item: MarketingTemplate },
												),
											]}
											className="mobile-utility-nav__link"
											type="search"
											id="mobile-utility-search"
											name="search"
										/>
										<button
											type="submit"
											onClick={() => mergeState({ isMobileSearchOpen: true })}
											className="mobile-utility-nav__link"
											aria-label="Search"
										/>
									</li>
								</div>
								<div className="mobile-search-modes">
									<p>Filter search by</p>
									<div className="mobile-search-form__search-modes">
										{searchModes && searchModes.length && (
											<ModesRadioButton
												searchModes={searchModes}
												onSearchClose={() =>
													mergeState({ isMobileSearchOpen: false })
												}
											/>
										)}
									</div>
								</div>
								<div className="mobile-search-modes">
									<p className="border-top">I&apos;m looking for...</p>
									<ul className="mobile-search-form__search-links">
										<li>
											<a href="/designations" aria-label="Designations">Designations</a>
										</li>
										<li>
											<a href="/free-courses" aria-label="Sample Courses">Sample Courses</a>
										</li>
										<li>
											<a href="/risk-and-insurance-topic-courses" aria-label="Topic Courses">
												Online Courses
											</a>
										</li>
										<li>
											<a href="/ceu" aria-label="CEU">CEU</a>
										</li>
									</ul>
								</div>
							</form>
						</div>
					)}
				</div>
			</div>
		</>
	);
};

export const CartUtilityNav = ({
	cartItemsLength,
	isLoaded,
	mergeState,
	state,
	searchModes,
}) => {
	const isTablet = useTabletStatus();
	const { isMobileSearchOpen } = state;

	React.useEffect(() => {
		const bodyEl = document.querySelector('body');
		if (isMobileSearchOpen) {
			bodyEl.classList.add('nav-is-open');
		} else {
			bodyEl.classList.remove('nav-is-open');
		}
	}, [isMobileSearchOpen]);
	return (
		<>
			{isTablet && (
				<>
					<li className="utility-nav__item">
						<button
							type="submit"
							onClick={() => mergeState({ isMobileSearchOpen: true })}
							className="mobile-utility-nav__link"
							aria-label="Search"
						>
							<svg
								className="u-icon-search-bar-dims"
								role="presentation"
								aria-hidden="true"
							>
								<use xlinkHref="#search-bar" />
							</svg>
						</button>

						{state.isMobileSearchOpen && (
							<MobileUtilitySearch
								searchModes={searchModes}
								onSearchClose={() => mergeState({ isMobileSearchOpen: false })}
							/>
						)}
					</li>
				</>
			)}
			<li className="utility-nav__item">
				<a href="/cart" className="utility-nav__link" title="Cart">
					<CartIcon className="utility-nav__icon" />
					<span className="u-text-uppercase utility-nav__label">
						Cart ({!isLoaded ? '-' : cartItemsLength || 0}
						<span className="u-sr-only"> Items</span>)
					</span>
				</a>
			</li>
		</>
	);
};
